import axios from 'axios';
import { API_URL, PLAYER_API_URL } from '../../configs/env.config';
import { PROGRAM_MODE, BROADCAST_MODE, AUTOMATED_MODE } from '../../constants/EditorMode';

/**
 * Fetch API - [GET] get program
 * @param id
 * @param token
 * @returns {Promise<*>}
 */
export const getProgram = async (id, token) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const response = await axios.get(`${API_URL}/programs/${id}`, options);
  if (response.data === '') {
    return {};
  }
  return response.data;
};

/**
 * Fetch API - [GET] get broadcast
 * @param id
 * @param token
 * @returns {Promise<*>}
 */
export const getBroadcast = async (id, token) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const response = await axios.get(`${API_URL}/broadcasts/${id}`, options);
  if (response.data === '') {
    return {};
  }
  return response.data;
};

/**
 * Fetch API - [GET] get automated broadcast
 * @param id
 * @param token
 * @returns {Promise<*>}
 */
export const getAutomatedBroadcast = async (id, token) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const response = await axios.get(`${API_URL}/automated/${id}`, options);
  if (response.data === '') {
    return {};
  }
  return response.data;
};

/**
 * Fetch API - [GET] get content library
 * @param token
 * @returns {Promise<*>}
 */
export const getContentLibrary = async (token) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const response = await axios.get(`${API_URL}/content`, options);
  return response.data;
};

/**
 * Fetch API - [GET] get content detail
 * @param id
 * @param token
 * @returns {Promise<{idError: *}>}
 */
export const getContentDetail = async (id, token) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    return await axios.get(`${API_URL}/content/${id}`, options);
    // return response.data;
  } catch (e) {
    return {
      ...e.response,
      idError: id
    };
    // throw new Error('We can\'t find the content. Did someone delete it? Please try again.');
  }
};

/**
 * Fetch API - [GET] get event data
 * @param token
 * @returns {Promise<Array>}
 */
export const getEventData = async (token) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.get(`${API_URL}/events`, options);
    return response.data;
  } catch (e) {
    return [];
  }
};

/**
 * API - [POST] create new event
 * @param name
 * @param token
 * @returns {Promise<{}>}
 */
export const createEvent = async (token, name) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const data = JSON.stringify({
    name
  });
  const response = await axios.post(`${API_URL}/events`, data, options);
  return response.data;
};

/**
 * API - [DELETE] delete event
 * @param id
 * @param token
 * @returns {Promise<{}>}
 */
export const deleteEvent = async (token, id) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const response = await axios.delete(`${API_URL}/events/${id}`, options);
  return response.data;
};

/**
 * API - [PUT] update event
 * @param id
 * @param newName
 * @param token
 * @returns {Promise<{}>}
 */
export const updateEvent = async (token, id, newName) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const data = JSON.stringify({
    name: newName
  });
  const response = await axios.put(`${API_URL}/events/${id}`, data, options);
  return response.data;
};

/**
 * API - [PUT] Save program
 * @param token
 * @param programData
 * @returns {Promise<void>}
 */
export const saveProgram = async (token, programData) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const data = JSON.stringify(programData);
  const response = await axios.put(`${API_URL}/programs`, data, options);
  return response.data;
};

/**
 * API - [PUT] Save broadcast
 * @param token
 * @param broadcastData
 * @returns {Promise<void>}
 */
export const saveBroadcast = async (token, broadcastData) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const data = JSON.stringify(broadcastData);
  const response = await axios.put(`${API_URL}/broadcasts`, data, options);
  return response.data;
};

/**
 * API - [PUT] Save automated broadcast
 * @param token
 * @param broadcastData
 * @returns {Promise<void>}
 */
export const saveAutomatedBroadcast = async (token, broadcastData) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const data = JSON.stringify(broadcastData);
  const response = await axios.put(`${API_URL}/automated`, data, options);
  return response.data;
};

/**
 * API - [GET] Search contents
 * @param token
 * @param keyword
 * @param page
 * @param size
 * @returns {Promise<void>}
 */
export const searchContent = async (token, keyword, page = 0, size = 20) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const response = await axios.get(`${API_URL}/content/search/${keyword}?page=${page}&size=${size}`, options);
  return response.data;
};

/**
 * API - [GET] Get desktop player data
 * @param type
 * @param journeyId
 * @param eventId
 * @param token
 * @returns {Promise<*>}
 */
export const getDesktopPlayerData = async (type, journeyId, token, eventId) => {
  try {
    let response;
    switch (type) {
      case BROADCAST_MODE:
        response = await axios.get(`${PLAYER_API_URL}/subscribers/play/${journeyId}?type=broadcast&secret=${token}`);
        break;
      case AUTOMATED_MODE:
        response = await axios.get(`${PLAYER_API_URL}/subscribers/play/${journeyId}?type=automated&secret=${token}`);
        break;
      case PROGRAM_MODE:
        response = await axios.get(`${PLAYER_API_URL}/subscribers/play/${journeyId}?type=program&secret=${token}&eventId=${eventId}`);
        break;
      default:
        throw new Error('Invalid type');
    }
    return response.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Send out sync - Journey
 */
export const sendOutSyncJourney = async (token, programData) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    const data = JSON.stringify(programData);
    await axios.put(`${API_URL}/programs/${programData.id}/sync`, data, options);
  } catch (err) {
    throw err;
  }
};

/**
 * Send out sync - Broadcast
 */
export const sendOutSyncBroadcast = async (token, broadcastData) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };

    let data = { ...broadcastData };
    data.contents = data.events[0].contents;
    delete data.events;
    data = JSON.stringify(broadcastData);

    await axios.put(`${API_URL}/broadcasts/${broadcastData.id}/sync`, data, options);
  } catch (err) {
    throw err;
  }
};

/**
 * Send out sync - Automated Broadcast
 */
export const sendOutSyncAutomated = async (token, automated) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };

    let data = { ...automated };
    data.contents = data.events[0].contents;
    delete data.events;
    data = JSON.stringify(automated);

    await axios.put(`${API_URL}/automated/${automated.id}/sync`, data, options);
  } catch (err) {
    throw err;
  }
};

/**
 * Fetch API - [GET] get list audiences
 * @param token
 * @returns {Promise<Array>}
 */
export const getAudiences = async (token) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.get(`${API_URL}/segments`, options);
    return response.data;
  } catch (err) {
    console.log(err.response);
    return [];
  }
};

/**
 * Fetch API - [GET] get list automated broadcast
 * @param token
 * @returns {Promise<Array>}
 */
export const getAutomatedBroadcasts = async (token) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.get(`${API_URL}/automated`, options);
    return response.data;
  } catch (err) {
    console.log(err.response);
    return [];
  }
};

/**
 * API - [PUT] update broadcast
 */
export const updateBroadcast = async (token, broadcastData) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const data = JSON.stringify(broadcastData);
  const response = await axios.put(`${API_URL}/broadcasts`, data, options);
  return response.data;
};

/**
 * API - [PUT] update automated broadcast
 */
export const updateAutomatedBroadcast = async (token, broadcastData) => {
  const options = {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  };
  const data = JSON.stringify(broadcastData);
  const response = await axios.put(`${API_URL}/automated`, data, options);
  return response.data;
};

/**
 * API - [GET] Get lock state
 * @param token
 * @param type
 * @param id
 * @returns {Promise<*>}
 */
export const getLockState = async (token, type, id) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.get(`${API_URL}/locking/${type}/${id}`, options);
    return response.data;
  } catch (err) {
    throw err;
  }
};

/**
 * API - [POST] Lock state
 * @param token
 * @param type
 * @param id
 * @returns {Promise<*>}
 */
export const lockState = async (token, type, id) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    const data = JSON.stringify({});
    const response = await axios.post(`${API_URL}/locking/${type}/${id}`, data, options);
    return response.data;
  } catch (err) {
    throw err;
  }
};

/**
 * API - [POST] Unlock state
 * @param token
 * @param type
 * @param id
 * @returns {Promise<*>}
 */
export const unlockState = async (token, type, id) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.delete(`${API_URL}/locking/${type}/${id}`, options);
    return response.data;
  } catch (err) {
    throw err;
  }
};

/**
 * API - [GET] Tenant settings
 * @param token
 * @returns {Promise<*>}
 */
export const getTenantSettings = async (token) => {
  try {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    const response = await axios.get(`${API_URL}/tenant/settings/`, options);
    return response.data;
  } catch (err) {
    throw err;
  }
};



// WEBPACK FOOTER //
// ./src/containers/program/api.js